import React, { useState } from "react";
import BreadcrumbChevron from "../../shared/components/BreadcrumbChevron";
import RefreshCwIcon from "../../shared/Icons/RefreshCwIcon";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import { useSeriesShowContext } from "../SeriesShowApp";
import SeriesPreviewModal from "./SeriesPreviewModal";
import ConfirmDialog from "./ConfirmDialog";
import SellPassesButton from "./SellPassesButton";
import ShowScopeChooser, { SCOPE_SERIES_ONLY, SCOPE_ALL_SHOWS, SCOPE_SELECTED } from "./ShowScopeChooser";
import { Button, Menu, copyText, Link01, Code01, SlashCircle01, Trash01, Edit01, Eye, ChevronDown } from "@opendate-app/setlist";
import { ticketWidgetTag } from "../../shared/CheckoutEmbedModal/embedSnippets";

const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatDate = (isoDate, options) => new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-US", options);

const formatSubline = (subline, listedAsCollection) => {
  const days = (subline.days_of_week || []).map(n => dayNames[n]).join("/");
  const fullDate = { month: "short", day: "numeric", year: "numeric" };
  const dateRange = !subline.start_date || !subline.end_date
    ? "Dates come from its shows"
    : listedAsCollection && subline.start_date === subline.end_date
      ? formatDate(subline.start_date, fullDate)
      : `${formatDate(subline.start_date, { month: "short", day: "numeric" })} — ${formatDate(subline.end_date, fullDate)}`;
  return { days, dateRange, venue: subline.venue_name };
};

const STATE_PILLS = {
  active:   { className: "series-header-state-active",   label: "Active series" },
  canceled: { className: "series-header-state-canceled", label: "Cancelled" },
  draft:    { className: "series-header-state-draft",    label: "Draft" },
  publishing: { className: "series-header-state-publishing", label: "Publishing…" },
};
const COMPLETED_PILL = { className: "series-header-state-completed", label: "Completed" };

const SeriesHeader = () => {
  const {
    series,
    teamId,
    canManageSeries,
    seriesDeletionBlockedReason,
    handleCancelSeries,
    handleDeleteSeries,
    listedAsCollection,
    collectionsPath,
  } = useSeriesShowContext();
  const noun = listedAsCollection ? "collection" : "series";
  const nounPossessive = listedAsCollection ? "collection's" : "series'";
  const { days, dateRange, venue } = formatSubline(series.subline_data, listedAsCollection);
  const statePill = STATE_PILLS[series.state] || COMPLETED_PILL;
  const statePillLabel = series.state === "active" && listedAsCollection ? "Active collection" : statePill.label;
  const isCanceled = series.state === "canceled";

  const [showPreview, setShowPreview] = useState(false);
  const [confirmType, setConfirmType] = useState(null);
  const [scope, setScope] = useState(SCOPE_ALL_SHOWS);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const closeConfirm = () => setConfirmType(null);

  const cancelableShows = (series.schedule || []).filter((row) => row.can_cancel && row.calendar_event_id);
  const showCount = cancelableShows.length;
  const pendingCount = (series.schedule || []).filter((row) => row.pending).length;

  // A recurring series' dates only exist because it spawned them, so cancelling the
  // run usually means cancelling them too. A collection's shows were each booked
  // independently and only grouped afterwards, so cancelling the collection must
  // not reach for them by default. Delete always opens on the non-destructive one.
  const defaultScope = (type) => {
    if (type !== "cancel") return SCOPE_SERIES_ONLY;
    // Already cancelled: there is no series-only step left, and no radio for it.
    if (isCanceled) return SCOPE_ALL_SHOWS;

    return series.grouping ? SCOPE_SERIES_ONLY : SCOPE_ALL_SHOWS;
  };

  const openConfirm = (type) => {
    setScope(defaultScope(type));
    setSelectedIds(new Set());
    setConfirmType(type);
  };

  const toggleShow = (calendarEventId) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      next.has(calendarEventId) ? next.delete(calendarEventId) : next.add(calendarEventId);
      return next;
    });
  };

  const chosenShowIds = () => (scope === SCOPE_SELECTED ? [...selectedIds] : []);

  // What the warning describes: everything cancellable for "all", the ticks for
  // "selected", nothing for series-only.
  const affectedShows = () => {
    if (scope === SCOPE_ALL_SHOWS) return cancelableShows;
    if (scope === SCOPE_SELECTED) return cancelableShows.filter((show) => selectedIds.has(show.calendar_event_id));
    return [];
  };

  const confirmDisabled = scope === SCOPE_SELECTED && selectedIds.size === 0;

  const baseUrl = window.location.origin;
  const landingUrl = `${baseUrl}/s/${series.public_permalink}`;
  const embedCode = series.buyer_widget
    ? ticketWidgetTag(`${baseUrl}/s/${series.public_permalink}/widget`, `od-series-${series.id}-iframe`, series.name || "Series")
    : `<script src="${baseUrl}/packs/od_embed.js"></script>
<iframe src="${landingUrl}?layout=iframe"
  id="od-series-${series.id}-iframe" title="${series.name || "Series"}"
  scrolling="no" style="border:none; width: 1px; min-width: 100%;" height="600px;"></iframe>
<script>
  document.addEventListener('DOMContentLoaded', function() {
    ODEmbed("od-series-${series.id}-iframe");
  });
</script>`;

  const copyToClipboard = async (text, label) => {
    if (await copyText(text)) {
      toastSuccess(`${label} copied to clipboard`);
    } else {
      toastError(`Could not copy ${label.toLowerCase()}. Please copy it manually.`);
    }
  };

  const shareActions = series.public_permalink ? [
    {
      label: "Copy Landing Page Link",
      icon: Link01,
      onClick: () => copyToClipboard(landingUrl, "Landing page link"),
    },
    {
      label: "Copy Embed Code",
      icon: Code01,
      onClick: () => copyToClipboard(embedCode, "Embed code"),
    },
  ] : [];

  const seriesActions = canManageSeries ? [
    ...(isCanceled && showCount === 0 ? [] : [{
      label: isCanceled ? `Cancel shows in this ${noun}` : `Cancel ${noun}`,
      icon: SlashCircle01,
      destructive: true,
      onClick: () => openConfirm("cancel"),
    }]),
    {
      label: `Delete ${noun}`,
      icon: Trash01,
      destructive: true,
      disabled: Boolean(seriesDeletionBlockedReason),
      description: seriesDeletionBlockedReason
        ? `Can't delete — ${seriesDeletionBlockedReason}.`
        : undefined,
      onClick: () => openConfirm("delete"),
    },
  ] : [];

  const menuActions = [...shareActions, ...seriesActions];

  const chosenIds = chosenShowIds();
  const hasPassProducts = (series.series_pass_products || []).length > 0;
  const soldCount = affectedShows().filter((show) => show.tickets_sold > 0).length;

  const soldPassCount = (series.series_pass_products || []).reduce((total, product) => total + (product.sold_count || 0), 0);
  const affectedIds = new Set(affectedShows().map((show) => show.calendar_event_id));
  const liveShowsLeft = (series.schedule || []).filter(
    (row) => row.calendar_event_id
      && row.classification !== "canceled"
      && !affectedIds.has(row.calendar_event_id)
      && row.upcoming,
  ).length;

  // Only the cancel dialog needs this: Delete is already disabled outright once a
  // pass has been sold, so the operator never reaches its body.
  const soldPassWarning = soldPassCount > 0 && liveShowsLeft === 0 && (
    <p className="series-confirm-warning">
      {soldPassCount === 1 ? "1 pass has been sold" : `${soldPassCount} passes have been sold`} for this {noun}.
      {" "}With no show left running {soldPassCount === 1 ? "it stops" : "they stop"} scanning at the door, and cancelling does not refund anyone.
    </p>
  );

  const soldWarning = soldCount > 0 && (
    <p className="series-confirm-warning">
      {soldCount === 1 ? "1 of the shows you're cancelling has sold tickets." : `${soldCount} of the shows you're cancelling have sold tickets.`}
      {" "}Cancelling does not refund anyone — refund or reschedule each show the way you normally would.
    </p>
  );

  // Pending dates are slots no show was ever created for. Cancelling stops the
  // series creating them, so say so rather than letting them vanish silently.
  const pendingNote = pendingCount > 0 && (
    <p>
      {pendingCount === 1
        ? "1 date that hasn't been created yet will be removed, since a cancelled series doesn't create new shows."
        : `${pendingCount} dates that haven't been created yet will be removed, since a cancelled series doesn't create new shows.`}
    </p>
  );

  const cancelBody = (
    <>
      <p>
        {showCount === 0
          ? `None of this ${nounPossessive} shows can be cancelled — they have already happened, or been cancelled already. Cancelling marks the ${noun} itself cancelled, which stops it selling passes.`
          : isCanceled
            ? `This ${noun} is already cancelled. Choose which of its shows to cancel:`
            : `Cancelling marks the ${noun} itself cancelled, which stops it selling passes. Choose what happens to its shows:`}
      </p>
      {showCount > 0 && (
        <ShowScopeChooser
          scope={scope}
          onScopeChange={setScope}
          name="series-cancel-scope"
          groupLabel="What to cancel"
          shows={cancelableShows}
          venueTimeZone={series.venue_time_zone}
          selectedIds={selectedIds}
          onToggle={toggleShow}
          seriesOnlyLabel={isCanceled ? null : `Cancel the ${noun} only — leave every show running`}
          allLabel={showCount === 1 ? `Cancel ${isCanceled ? "its" : `the ${noun} and its`} 1 remaining show` : `Cancel ${isCanceled ? "all" : `the ${noun} and all`} ${showCount} remaining shows`}
        />
      )}
      {!isCanceled && pendingNote}
      {soldWarning}
      {soldPassWarning}
    </>
  );

  const deleteBody = (
    <>
      <p>
        {showCount > 0
          ? `Deleting removes the ${noun} itself. Your shows are never deleted — choose whether they keep running or get cancelled first:`
          : `Deleting removes the ${noun} itself. Your shows are never deleted.`}
      </p>
      {showCount > 0 ? (
        <ShowScopeChooser
          scope={scope}
          onScopeChange={setScope}
          name="series-delete-scope"
          groupLabel="What to delete"
          shows={cancelableShows}
          venueTimeZone={series.venue_time_zone}
          selectedIds={selectedIds}
          onToggle={toggleShow}
          seriesOnlyLabel={`Remove the ${noun} only — leave every show running`}
          allLabel={showCount === 1 ? `Remove the ${noun} and cancel its 1 remaining show` : `Remove the ${noun} and cancel all ${showCount} remaining shows`}
        />
      ) : (
        <p>None of its shows can be cancelled, so they are all left as they are.</p>
      )}
      {soldWarning}
      {hasPassProducts && (
        <p className="series-confirm-warning">
          The passes and packs set up on this {noun} are removed with it.
        </p>
      )}
      <p className="series-confirm-warning">
        {scope === SCOPE_SERIES_ONLY
          ? `The ${noun} itself is gone for good, though you can group these shows again later.`
          : "This cannot be undone."}
      </p>
    </>
  );

  return (
    <div className="series-header">
      <nav aria-label="breadcrumb" className="mb-3">
        <div className="d-flex align-items-center flex-wrap series-breadcrumb">
          <a href="/dashboard" className="settings-breadcrumb-link">Calendar</a>
          <BreadcrumbChevron />
          {listedAsCollection
            ? <a href={collectionsPath} className="settings-breadcrumb-link">Collections</a>
            : <a href={`/teams/${teamId}/event_series`} className="settings-breadcrumb-link">Series</a>}
          <BreadcrumbChevron />
          <span className="settings-breadcrumb-current">{series.name}</span>
        </div>
      </nav>
      <div className="d-flex align-items-center series-header-title-row">
        <h1 className="series-header-title">{series.name}</h1>
        <span className={`series-header-state-pill ${statePill.className}`}>
          {statePillLabel}
        </span>
        <div className="ml-auto d-flex align-items-center series-header-actions">
          <SellPassesButton variant="secondary" />
          {series.public_permalink && (
            <Button variant="secondary" icon={Eye} onClick={() => setShowPreview(true)}>
              Preview
            </Button>
          )}
          <Button
            variant="secondary"
            icon={Edit01}
            as="a"
            href={`/teams/${teamId}/event_series/${series.id}/edit`}
          >
            Edit
          </Button>
          {menuActions.length > 0 && (
            <Menu
              items={menuActions}
              placement="bottom-end"
              trigger={(
                <Button variant="primary" aria-label={listedAsCollection ? "Collection actions" : "Series actions"}>
                  Actions
                  <ChevronDown width={16} height={16} aria-hidden="true" className="series-header-actions-caret" />
                </Button>
              )}
            />
          )}
        </div>
      </div>
      <ConfirmDialog
        open={confirmType === "cancel"}
        title={isCanceled ? `Cancel shows in this ${noun}?` : `Cancel this ${noun}?`}
        body={cancelBody}
        confirmLabel={isCanceled ? "Cancel shows" : `Cancel ${noun}`}
        confirmDisabled={confirmDisabled}
        onConfirm={() => handleCancelSeries(scope, chosenIds)}
        onClose={closeConfirm}
      />
      <ConfirmDialog
        open={confirmType === "delete"}
        title={`Delete this ${noun}?`}
        body={deleteBody}
        confirmLabel={`Delete ${noun}`}
        confirmDisabled={confirmDisabled}
        onConfirm={() => handleDeleteSeries(scope, chosenIds)}
        onClose={closeConfirm}
      />
      {showPreview && (
        <SeriesPreviewModal series={series} onClose={() => setShowPreview(false)} />
      )}
      <div className="d-flex align-items-center flex-wrap series-header-subline">
        {!series.grouping && (
          <span className="d-inline-flex align-items-center series-header-subline-item">
            <RefreshCwIcon width="12" height="12" />
            {`Every ${days}`}
          </span>
        )}
        <span className="d-inline-flex align-items-center series-header-subline-item">
          <i className="fa fa-calendar series-header-subline-icon" />
          {dateRange}
        </span>
        <span className="d-inline-flex align-items-center series-header-subline-item">
          <i className="fa fa-store series-header-subline-icon" />
          {venue}
        </span>
      </div>
    </div>
  );
};

export default SeriesHeader;
